import { useMemo, useCallback, useEffect } from 'react';
import Select from 'react-select';
import FeatherIcon from 'feather-icons-react';
import { Form, Row, Col, Label, Button, FormGroup } from "reactstrap";

const PropertySelector = ({ formik, propertyList, onAddClick }) => {
    const selectedProperty = useMemo(() =>
        propertyList.find(prop => prop._id === formik.values.propertyId),
        [propertyList, formik.values.propertyId]
    );

    const buildingList = useMemo(() => {
        if (!selectedProperty?.building) return [];
        return selectedProperty.building
            .filter(b => b && b.buildingName)
            .map(b => b.buildingName);
    }, [selectedProperty]);

    useEffect(() => {
        if (buildingList.length === 1 && !formik.values.buildingName) {
            formik.setFieldValue('buildingName', buildingList[0]);
        }
        if (buildingList.length === 0 && formik.values.buildingName) {
            formik.setFieldValue('buildingName', '');
        }
    }, [buildingList, formik]);

    const isAddButtonDisabled = useMemo(() => {
        const hasPropertyId = Boolean(formik.values.propertyId);
        const hasBuildingName = Boolean(formik.values.buildingName);

        if (buildingList.length > 0 && !hasBuildingName) {
            return true;
        }

        return !hasPropertyId;
    }, [formik.values.propertyId, formik.values.buildingName, buildingList.length]);

    const propertyOptions = useMemo(() =>
        propertyList.map(prop => ({
            value: prop._id,
            label: `${prop.title}, ${prop.location}`
        })),
        [propertyList]
    );

    const buildingOptions = useMemo(() =>
        buildingList.map(building => ({
            value: building,
            label: building
        })),
        [buildingList]
    );

    const selectedPropertyOption = useMemo(() =>
        propertyOptions.find(option => option.value === formik.values.propertyId) || null,
        [propertyOptions, formik.values.propertyId]
    );

    const selectedBuildingOption = useMemo(() =>
        buildingOptions.find(option => option.value === formik.values.buildingName) || null,
        [buildingOptions, formik.values.buildingName]
    );

    const handlePropertyChange = useCallback((selectedOption) => {
        const newPropertyId = selectedOption ? selectedOption.value : '';
        formik.setValues({
            ...formik.values,
            propertyId: newPropertyId,
            buildingName: '',
        });
    }, [formik]);

    const handleBuildingChange = useCallback((selectedOption) => {
        formik.setFieldValue('buildingName', selectedOption ? selectedOption.value : '');
    }, [formik]);

    const customSelectStyles = {
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999
        }),
        menu: (base) => ({
            ...base,
            zIndex: 9999
        }),
        control: (base, state) => ({
            ...base,
            zIndex: 2,
            backgroundColor: state.isDisabled ? '#f8f9fa' : base.backgroundColor,
            cursor: state.isDisabled ? 'not-allowed' : 'default'
        })
    };

    return (
        <Form className='mb-3'>
            <Row>
                <Col md={Boolean(onAddClick) ? 5 : 6}>
                    <FormGroup>
                        <Label>Property</Label>
                        <Select
                            value={selectedPropertyOption}
                            onChange={handlePropertyChange}
                            options={propertyOptions}
                            placeholder="Select Property"
                            isClearable
                            name="propertyId"
                            className="react-select-container"
                            classNamePrefix="react-select"
                            onBlur={() => formik.setFieldTouched('propertyId', true)}
                            styles={customSelectStyles}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                        />
                        {formik.touched.propertyId && formik.errors.propertyId && (
                            <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                                {formik.errors.propertyId}
                            </div>
                        )}
                    </FormGroup>
                </Col>

                {buildingList.length > 0 && (
                    <Col md={Boolean(onAddClick) ? 5 : 6}>
                        <FormGroup>
                            <Label>Building Name</Label>
                            <Select
                                value={selectedBuildingOption}
                                onChange={handleBuildingChange}
                                options={buildingOptions}
                                placeholder="Select Building"
                                isClearable={buildingList.length > 1}
                                isDisabled={buildingList.length === 1}
                                name="buildingName"
                                className="react-select-container"
                                classNamePrefix="react-select"
                                onBlur={() => formik.setFieldTouched('buildingName', true)}
                                styles={customSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                            />
                            {formik.touched.buildingName && formik.errors.buildingName && (
                                <div className="text-danger" style={{ fontSize: '0.75rem', marginTop: '4px' }}>
                                    {formik.errors.buildingName}
                                </div>
                            )}
                        </FormGroup>
                    </Col>
                )}

                {onAddClick && <Col md={2} className="d-flex align-items-end">
                    <Button
                        color="primary"
                        className="ms-2"
                        onClick={onAddClick}
                        disabled={isAddButtonDisabled}
                    >
                        <FeatherIcon icon="plus-circle" size={18} style={{ marginRight: '8px' }} />
                        Add Orders
                    </Button>
                </Col>}
            </Row>
        </Form>
    );
};

export { PropertySelector };